import { Component, OnInit } from '@angular/core';
import { ConfigService } from './config.service';
import { MapService } from './map.service';
import { FieldsetModule } from 'primeng/fieldset';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	UntypedFormControl,
	Validators } from '@angular/forms';

@Component({
	selector: 'config-preferences-component',
	standalone: false,
	templateUrl: 'config-preferences.component.html',
	styleUrls: ['config-preferences.component.css']
})
export class ConfigPreferencesComponent implements OnInit {
	configService: ConfigService;
	mapService: MapService;
	formBuilder: UntypedFormBuilder;
	baseMaps: any[] = [];
	userPreferences: any;

	// for developer area
	developerMode: boolean;
	developerDate: string;

	configForm: UntypedFormGroup;
	baseMapSelect: UntypedFormControl;
	latitude: UntypedFormControl;
	longitude: UntypedFormControl;
	zoom: UntypedFormControl;

	// tooltip titles
	zoomTitle = `zoom level details:
	1: World
	5: Landmass/Continent
	10: City
	15: Streets
	20: Buildings`;
	latitudeTitle = 'Latitude of map center';
	longitudeTitle = 'Longitude of map center';
	baseMapTitle = 'Background map layer';

	constructor (
		configService: ConfigService,
		mapService: MapService,
		formBuilder: UntypedFormBuilder ) {
			this.configService = configService;
			this.mapService = mapService;
			this.formBuilder = formBuilder;
		}

	ngOnInit () {
		let tmpPref = this.configService.getUserPreferences();
		this.userPreferences = this.cloneObj ( tmpPref );
		console.log(this.userPreferences);
		this.developerMode = this.configService.getUseDeveloperDate();
		this.developerDate = this.configService.getDeveloperDate() ||  "";

		// set up base map pulldown options
		// tslint:disable-next-line:forin
		for (const key in this.mapService.baseMaps) {
				this.baseMaps.push (key);
		}

		this.baseMapSelect = new UntypedFormControl( this.userPreferences.preferences.map.baseMap, Validators.required );
		this.latitude = new UntypedFormControl( this.userPreferences.preferences.map.latitude, Validators.required );
		this.longitude = new UntypedFormControl( this.userPreferences.preferences.map.longitude, Validators.required );
		this.zoom = new UntypedFormControl( this.userPreferences.preferences.map.zoom, Validators.required );

		// Every time a new configuration is added, the following steps should be taken:
		//   * add FormControl to this class description above
		//   * add a new name to the formBuilder group below
		//   * connect the FormControl to the name in the formBuilder below
		// This is the end of adding it to this area, but config.service.ts load routine must be addressed.
		// The configuration must be added to the actual userPreferences.json file.
		this.configForm = this.formBuilder.group({
			'baseMapSelect': this.baseMapSelect,
			'latitude': this.latitude,
			'longitude': this.longitude,
			'zoom': this.zoom
		});

		// Cat - figure out how to change color of button back once submit has been clicked.
		this.baseMapSelect.valueChanges.subscribe ( value => this.userPreferences.preferences.map.baseMap = value);
		this.latitude.valueChanges.subscribe ( value => this.userPreferences.preferences.map.latitude = value);
		this.longitude.valueChanges.subscribe ( value => this.userPreferences.preferences.map.longitude = value);
		this.zoom.valueChanges.subscribe ( value => this.userPreferences.preferences.map.zoom = value);
	}

	// At this point, any changes the user has made has not been saved or reflected in any objects (map, etc).
	// We need to save in both places: configService and additional service (mapService, etc.)
	// We also need to save to user web services.
	onSubmit(): void {
		console.log('Saving settings');
		const up = this.userPreferences.preferences;
		console.log(up);

		// basemap layer
		this.mapService.setActiveTileLayer(
			this.mapService.baseMaps[
				up.map.baseMap]);
		this.configService.setUserPreference(
			'preferences.map.baseMap', up.map.baseMap);

		// zoom
		this.mapService.setCurrentZoom(up.map.zoom);
		this.configService.setUserPreference(
			'preferences.map.zoom', +up.map.zoom);

		// current center
		this.mapService.setCurrentCenterWithLatLon(
			+up.map.latitude, +up.map.longitude);
		this.configService.setUserPreference(
			'preferences.map.latitude', +up.map.latitude);
		this.configService.setUserPreference(
			'preferences.map.longitude', +up.map.longitude);

		// Now save it to user web services
		this.configService.writeUserPreferences();
	}

	reset() {
		// Currently, this routine just clears the fields - not desired.  Need to decide if this
		// routine should be fully implemented.  Is a reset desired?
		this.configForm.reset();
	}

	// utility function to clone our user preferences.
	// source: http://blog.soulserv.net/understanding-object-cloning-in-javascript-part-ii/
	cloneObj( original ) {
		// First create an empty object with
		// same prototype of our original source
		const clone = Object.create( Object.getPrototypeOf( original ) ) ;

		let i, descriptor;
		const keys = Object.getOwnPropertyNames( original ) ;

		for ( i = 0 ; i < keys.length ; i ++ ) {
			// Save the source's descriptor
			descriptor = Object.getOwnPropertyDescriptor( original , keys[ i ] ) ;

			if ( descriptor.value && typeof descriptor.value === 'object' ) {
				// If the value is an object, recursively deepCopy() it
				descriptor.value = this.cloneObj( descriptor.value ) ;
			}

			Object.defineProperty( clone , keys[ i ] , descriptor ) ;
		}

		return clone ;
	}

	// As the user clicks the developer mode checkbox, this toggles the boolean developerMode value
	toggleDeveloperMode () {
		this.developerMode = !this.developerMode;
		// console.log("Setting developerMode to " + this.developerMode );
	}

	// Once the user modifies anything in the developer area, the Set button must be pushed to save those settings...
	saveDeveloperSettings () {
		// developerData.developerMode = this.developerMode;
		console.log("Setting display date to " + this.developerDate);
		this.configService.systemConfiguration.OperatorInterfaceDemoMode.periodEndDate = this.developerDate;
		this.configService.appReference.refreshDataAndTime();
		this.configService.setUseDeveloperDate(this.developerMode);
		this.configService.setDeveloperDate(this.developerDate);
	}

	doRequest() {
		const data = null;
		const xhr = new XMLHttpRequest();

		// create a listener for the XMLHttpRequest to process the response
		xhr.addEventListener('readystatechange', function () {
			// readyState returns the state an XMLHttpRequest client is in; 4 indicates the operation is complete
			if (this.readyState === 4) {
				console.log(this.responseText);
				// The response is a string of data, separates by ","
				// let res = this.responseText.split(',');
			}
		});

		// create the request
		xhr.open('GET', 'http://192.168.1.77:8080/operatorui-rest/server-test/?json');
		// xhr.setRequestHeader("app_id", "783bdfd5");
		// xhr.setRequestHeader("app_key", "da030411e419fbdaceeb5197dd76de5f");
		xhr.send(data);
	}
}
